import { Box, Button, Dialog, DialogActions, Typography } from "@mui/material";
import { merchantTableColumns, mearchatSkuDummyData } from "./data";
import { useEffect, useState } from "react";
import { converAttrToDatagridCol, createdByColumns, selectOptionsKeyName } from "data/globalData";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import axios from "axios";
import { FileDownload } from "@mui/icons-material";
import DatagridWithApiPagination from "components/ReusableComponents/DatagridWithApiPagination/DatagridWithApiPagination";
import SkuSection from "../CreateShoot/SkuSection/SkuSection";
import { shootMerchantSampleDetailsColumns, shootSkuIcodeColumn } from "../ShootsDashboard/data";
import { set } from "lodash";
import RenderInputField from "components/PimDashboard/CommonComponents/RenderInputField/RenderInputField";
import { grey } from "@mui/material/colors";
import SampleHistoryModal from "./SampleHistoryModal/SampleHistoryModal";
import { custom } from "data/authData";

const MerchantData = () => {
	const { user } = AuthState();
	const { setLoadingOpen, setSnackBarOpen, setSnackBar, snackBar } = GlobalState();
	const disableEdit = user?.role?.[custom.SAMPLE_INWARD_VIEWER.key] === true;

	const [selectedSkus, setSelectedSkus] = useState([]);
	const [allSkus, setAllSkus] = useState([]);
	const [editModal, setEditModal] = useState(false);
	const [historyModal, setHistoryModal] = useState(false);
	const [editedData, setEditedData] = useState(null);
	const [selectedHistorySku, setSelectedHistorySku] = useState(null);
	const [bulkEdit, setBulkEdit] = useState(false);

	useEffect(() => {
		if (selectedSkus.length > 1) setBulkEdit(true);
		else setBulkEdit(false);
	}, [selectedSkus]);

	const handleEditModalClose = () => {
		setEditModal(false);
		setEditedData(null);
		setSelectedSkus([]);
	};

	const handleIcodeSelect = (data) => {
		setEditedData(data);
		setEditModal(true);
	};

	const handleBulkEditButtonClick = () => {
		if (selectedSkus.length === 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select at least one SKU to edit",
				severity: "warning",
			});
			return;
		}
		setEditModal(true);
	};
	const handleDataSave = async (data) => {
		let payload = {};
		Object.keys(shootMerchantSampleDetailsColumns).forEach((key) => {
			if (data[key]) {
				payload[key] = data[key];
			}
		});
		setLoadingOpen(true);
		if (bulkEdit)
			await axios
				.put(`api/studio/bulk_update_shoot_merchant_sample_details`, {
					body: payload,
					icode_list: selectedSkus,
				})
				.then((res) => {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						open: true,
						message: "Data updated successfully",
						severity: "success",
					});
					setLoadingOpen(false);
					handleEditModalClose();
					setSelectedSkus([]);
					setAllSkus((prev) => {
						return prev.map((item) => {
							if (selectedSkus.includes(item[shootSkuIcodeColumn.key])) {
								return { ...item, ...data };
							}
							return item;
						});
					});
				})
				.catch((err) => {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						severity: "error",
						message: parseErrorMessage(err),
					});
					setLoadingOpen(false);
				});
		else
			await axios
				.put(`api/studio/update_shoot_merchant_sample_details/${data[shootSkuIcodeColumn.key]}`, payload)
				.then((res) => {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						open: true,
						message: "Data updated successfully",
						severity: "success",
					});
					setLoadingOpen(false);
					handleEditModalClose();
					setSelectedSkus([]);
					setAllSkus((prev) => {
						return prev.map((item) => {
							if (item[shootSkuIcodeColumn.key] === data[shootSkuIcodeColumn.key]) {
								return { ...item, ...data };
							}
							return item;
						});
					});
				})
				.catch((err) => {
					setSnackBarOpen(true);
					setSnackBar({
						...snackBar,
						severity: "error",
						message: parseErrorMessage(err),
					});
					setLoadingOpen(false);
					setSelectedSkus([]);
				});
	};

	const handleHistoryClick = (selectedRow) => {
		setSelectedHistorySku(selectedRow);
		setHistoryModal(true);
	};

	return (
		<Box display="flex" width="90vw" flexWrap="wrap" margin="auto">
			<Box width="100%" alignItems="flex-end" display="flex" justifyContent="flex-end">
				<Button variant="contained" disabled={!bulkEdit} onClick={handleBulkEditButtonClick}>
					Bulk Edit
				</Button>
			</Box>
			<Box pt={1} width="100%">
				<SkuSection
					selectedSkus={selectedSkus}
					setSelectedSkus={setSelectedSkus}
					shootData={{}}
					onRowClick={handleIcodeSelect}
					minHeight={"72vh"}
					maxHeight={"72vh"}
					skusState={allSkus}
					setSkusState={setAllSkus}
					hideSearchHeader={true}
					showActionColumn={true}
					actionList={[{ name: "History", onClick: handleHistoryClick }]}
					disableSelection={disableEdit}
				/>
			</Box>
			<Dialog fullWidth maxWidth="lg" open={editModal} onClose={handleEditModalClose}>
				<Typography variant="h6" p={2} pb={1}>
					{bulkEdit ? "Bulk" : ""}Edit Merchant Sample Details
				</Typography>
				<Box sx={{ p: 2, pt: 1, borderBottom: "1px solid #ccc" }}>
					{/* disply icode chips */}
					{bulkEdit ? (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
							<Typography variant="h6" fontSize={16} sx={{ width: "100%" }} color={grey[700]}>
								Selected Icodes
							</Typography>
							{selectedSkus.map((sku) => (
								<Box
									key={sku}
									sx={{
										backgroundColor: grey[400],
										borderRadius: "5px",
										padding: "5px",
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}>
									<Typography variant="subtitle1">{sku}</Typography>
								</Box>
							))}
						</Box>
					) : (
						<Typography variant="subtitle1">SKU ICODE: {editedData?.[shootSkuIcodeColumn.key]}</Typography>
					)}
				</Box>
				<Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
					{Object.values(shootMerchantSampleDetailsColumns).map((column) => {
						return (
							<RenderInputField
								key={column.key}
								column={column}
								data={editedData}
								setData={setEditedData}
								selectOptObjName={selectOptionsKeyName}
								width="32%"
								longWidth="66%"
							/>
						);
					})}
				</Box>
				<DialogActions>
					<Button variant="outlined" onClick={handleEditModalClose}>
						Cancel
					</Button>
					<Button
						autoFocus
						variant="contained"
						sx={{ mr: 1 }}
						onClick={() => handleDataSave(editedData)}
						disabled={disableEdit}>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			<SampleHistoryModal skuDetails={selectedHistorySku} modal={historyModal} setModal={setHistoryModal} />
		</Box>
	);
};

export default MerchantData;
